import type { NewsItem } from "@/mocks/news";
import { hotRank, tickerItems, newsList, categories } from "@/mocks/news";

type Props = {
  onOpen: (a: NewsItem) => void;
  onKeyword: (w: string) => void;
};

function catLabel(key: string) {
  const found = categories.find((c) => c.key === key);
  return found ? found.label : key;
}

const quickLive = [
  { time: "14:20", text: "跨年音乐季加场与抢票时间线公布" },
  { time: "13:46", text: "时尚盛典第二波高清红毯图释出" },
  { time: "12:33", text: "高分悬疑剧结局解析帖刷屏" },
  { time: "11:08", text: "年度大片首支预告播放量破亿" },
];

export default function Hero({ onOpen, onKeyword }: Props) {
  const featured = newsList.filter((n) => n.featured)[0] ?? newsList[0];

  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 pt-6 pb-10" id="top">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* 主头条 */}
        <article className="group lg:col-span-2">
          <button
            onClick={() => onOpen(featured)}
            className="relative block w-full cursor-pointer overflow-hidden rounded-2xl text-left"
            aria-label={featured.title}
          >
            <div className="h-72 w-full overflow-hidden md:h-[420px]">
              <img
                src={featured.image}
                alt={featured.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-foreground-950/10"></div>
            <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-signal-tower-fill"></i> 头条直播
            </span>
            <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-foreground-950/50 px-3 py-1 text-xs text-background-50 backdrop-blur">
              <i className="ri-fire-line text-accent-400"></i> 热度 {featured.views}
            </span>
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-medium text-background-50">
                  {catLabel(featured.category)}
                </span>
                <span className="text-xs text-background-200">{featured.time}</span>
              </div>
              <h2 className="font-heading text-xl font-black leading-snug text-background-50 md:text-3xl">
                {featured.title}
              </h2>
              <p className="mt-2 hidden text-sm leading-relaxed text-background-200 md:line-clamp-2">{featured.summary}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-sm font-medium text-foreground-900 hover:bg-accent-400 transition-colors">
                阅读全文 <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </button>

          {/* 副头条双卡 */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {newsList.filter((n) => n.featured).slice(1, 3).map((n) => (
              <button
                key={n.id}
                onClick={() => onOpen(n)}
                className="group flex gap-3 rounded-xl bg-background-100 p-2.5 text-left hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <div className="h-24 w-32 flex-shrink-0 overflow-hidden rounded-lg">
                  <img src={n.image} alt={n.title} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="min-w-0 py-1">
                  <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[11px] font-medium text-accent-900">{catLabel(n.category)}</span>
                  <p className="mt-1.5 line-clamp-2 text-sm font-medium leading-snug text-foreground-900">{n.title}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">{n.time}</p>
                </div>
              </button>
            ))}
          </div>
        </article>

        {/* 右侧：热搜 + 实时 */}
        <aside className="flex flex-col gap-6">
          <div className="rounded-2xl bg-background-100 p-5">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-fire-fill text-primary-500"></i> 实时热搜榜
              </h3>
              <span className="text-xs text-foreground-500">每分钟刷新</span>
            </div>
            <ol className="mt-4 space-y-2.5">
              {hotRank.slice(0, 5).map((h, i) => (
                <li key={h.word}>
                  <button
                    onClick={() => onKeyword(h.word)}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <span
                      className={`w-5 text-center font-heading text-base font-black ${
                        i < 3 ? "text-primary-500" : "text-foreground-400"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground-800">{h.word}</span>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{h.heat}</span>
                  </button>
                </li>
              ))}
            </ol>
            <button
              onClick={() => onKeyword("#热门话题#")}
              className="mt-3 w-full rounded-md bg-primary-500 px-3 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              查看完整热搜榜
            </button>
          </div>

          <div className="rounded-2xl bg-background-100 p-5">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-flashlight-fill text-accent-600"></i> 实时快讯
            </h3>
            <ul className="mt-4 space-y-3">
              {quickLive.map((q, i) => (
                <li key={i} className="flex gap-3">
                  <div className="relative mt-1 flex h-3 w-3 items-center justify-center">
                    <span className={`h-2 w-2 rounded-full ${i < 2 ? "cap-dot relative bg-primary-500" : "bg-secondary-400"}`}></span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm leading-snug text-foreground-800">{q.text}</p>
                    <p className="mt-0.5 text-[11px] text-foreground-500">今天 {q.time}</p>
                  </div>
                </li>
              ))}
            </ul>
            <button
              onClick={() => onKeyword("快讯")}
              className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              查看全部快讯 <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </aside>
      </div>

      {/* 顶部滚动条 */}
      <div className="mt-6 flex items-center overflow-hidden rounded-full border border-background-200 bg-background-50">
        <span className="flex flex-shrink-0 items-center gap-1.5 rounded-l-full bg-primary-500 px-4 py-2 text-xs font-medium text-background-50">
          <i className="ri-loop-left-line"></i> 滚动快讯
        </span>
        <div className="relative flex-1 overflow-hidden py-2">
          <div className="cap-ticker-track text-sm text-foreground-700">
            {tickerItems.concat(tickerItems).map((t, i) => (
              <span key={i} className="mx-6 flex items-center gap-2 whitespace-nowrap">
                <i className="ri-arrow-right-s-line text-primary-500"></i>{t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}